import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Snackbar, Alert, Fade, styled } from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.03)'
    : 'rgba(0, 0, 0, 0.02)',
  border: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.08)'
  }`,
  borderRadius: theme.shape.borderRadius * 1.5,
  padding: theme.spacing(2.5),
  transition: theme.transitions.create(
    ['background-color', 'border-color', 'box-shadow'],
    {
      duration: theme.transitions.duration.short
    }
  ),
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.04)'
      : 'rgba(0, 0, 0, 0.03)',
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.15)'
      : 'rgba(0, 0, 0, 0.12)',
    boxShadow: `0 2px 8px ${
      theme.palette.mode === 'dark'
        ? 'rgba(0, 0, 0, 0.25)'
        : 'rgba(0, 0, 0, 0.05)'
    }`
  },
  '&::-webkit-scrollbar': {
    width: '6px',
    height: '6px'
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    borderRadius: '3px'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '3px',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.3)'
        : 'rgba(0, 0, 0, 0.3)'
    }
  }
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.5),
  transition: theme.transitions.create(
    ['background-color', 'transform', 'color'],
    {
      duration: theme.transitions.duration.shorter
    }
  ),
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.04)',
    transform: 'scale(1.1)'
  }
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[6],
  '& .MuiAlert-icon': {
    fontSize: '1.25rem'
  }
}));

const JsonDataDisplay = ({
  data,
  title,
  sx = {},
  titleProps = {},
  showCopyButton = true,
  ...props
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  const formatData = (data) => {
    if (!data) return 'No data available';
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return data;
      }
    }

    return JSON.stringify(data, null, 2);
  };

  const handleCopy = async () => {
    try {
      const textToCopy = formatData(data);
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCloseCopySuccess = () => {
    setCopySuccess(false);
  };

  return (
    <>
      <Box sx={{ mb: 3, ...sx }} {...props}>
        {title && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1.5,
              px: 0
            }}
          >
            <Typography
              variant='subtitle2'
              color='text.primary'
              sx={{
                fontWeight: 600,
                letterSpacing: '0.02em',
                fontSize: '0.875rem'
              }}
              {...titleProps}
            >
              {title}
            </Typography>

            {showCopyButton && data && (
              <Tooltip
                title='Copy to clipboard'
                placement='top'
                arrow
              >
                <StyledIconButton
                  size='small'
                  onClick={handleCopy}
                >
                  <ContentCopyIcon fontSize='small' />
                </StyledIconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <StyledBox
          sx={{
            position: 'relative'
          }}
        >
          <Fade in={true} timeout={300}>
            <Box sx={{ backgroundColor: 'none' }}>
              {data && data !== null && data !== undefined && data !== '' && data !== 'null' && data !== 'undefined' && data !== '[]' && data !== '{}' ? (
                <Typography
                  component='pre'
                  sx={{
                    fontFamily: "'Fira Code', 'Monaco', 'Consolas', 'Courier New', monospace",
                    fontSize: '0.8125rem',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                    margin: 0,
                    lineHeight: 1.6,
                    letterSpacing: '0.01em',
                    color: (theme) =>
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.95)'
                        : 'rgba(0, 0, 0, 0.87)'
                  }}
                >
                  {formatData(data)}
                </Typography>
              ) : (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 3,
                    opacity: 0.7
                  }}
                >
                  No data available
                </Typography>
              )}
            </Box>
          </Fade>
        </StyledBox>
      </Box>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={handleCloseCopySuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <StyledAlert
          onClose={handleCloseCopySuccess}
          severity='success'
          variant='filled'
          sx={{ minWidth: 200 }}
        >
          Copied to clipboard
        </StyledAlert>
      </Snackbar>
    </>
  );
};

export default JsonDataDisplay;
